/**
 * Tipos TypeScript para el módulo de Reportes y Dashboards
 * Corresponde a los schemas del backend en app/schemas/reporte.py
 */

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Tipos de reportes disponibles en el sistema
 */
export enum TipoReporte {
  DASHBOARD = 'DASHBOARD',
  OPERATIVO = 'OPERATIVO',
  GERENCIAL = 'GERENCIAL',
  CLIENTE = 'CLIENTE',
  EXPORTABLE = 'EXPORTABLE'
}

/**
 * Períodos predefinidos para filtrar reportes
 */
export enum PeriodoReporte {
  HOY = 'HOY',
  AYER = 'AYER',
  SEMANA = 'SEMANA',
  SEMANA_PASADA = 'SEMANA_PASADA',
  MES = 'MES',
  MES_PASADO = 'MES_PASADO',
  TRIMESTRE = 'TRIMESTRE',
  ANO = 'ANO',
  PERSONALIZADO = 'PERSONALIZADO'
}

/**
 * Tipos de widgets disponibles para visualización de datos
 */
export enum TipoWidget {
  CONTADOR = 'CONTADOR',
  GRAFICO_LINEA = 'GRAFICO_LINEA',
  GRAFICO_BARRA = 'GRAFICO_BARRA',
  GRAFICO_PIE = 'GRAFICO_PIE',
  GRAFICO_DONA = 'GRAFICO_DONA',
  TABLA = 'TABLA',
  LISTA = 'LISTA',
  PROGRESO = 'PROGRESO',
  TENDENCIA = 'TENDENCIA'
}

/**
 * Categorías de métricas para clasificación
 */
export enum CategoriaMetrica {
  CONVERSACION = 'CONVERSACION',
  TICKET = 'TICKET',
  ACTIVIDAD = 'ACTIVIDAD',
  TIEMPO = 'TIEMPO',
  CLIENTE = 'CLIENTE',
  USUARIO = 'USUARIO',
  CONOCIMIENTO = 'CONOCIMIENTO'
}

// ============================================================================
// INTERFACES PARA MÉTRICAS
// ============================================================================

/**
 * Definición de una métrica del sistema
 */
export interface Metrica {
  id: string;
  codigo: string;
  nombre: string;
  descripcion?: string;
  categoria: CategoriaMetrica;
  tipo_widget_default: TipoWidget;
  formato: string;
  icono?: string;
}

/**
 * Valor calculado de una métrica en un período específico
 */
export interface MetricaValor {
  codigo: string;
  nombre: string;
  valor: number | string | any[];
  valor_formateado: string;
  tendencia?: number;
  tendencia_direccion?: 'up' | 'down' | 'stable';
  periodo: string;
  actualizado_at: string;
}

// ============================================================================
// INTERFACES PARA WIDGETS
// ============================================================================

/**
 * Configuración de un widget en el dashboard
 */
export interface WidgetConfig {
  posicion?: { x: number; y: number; w: number; h: number };
  colores?: string[];
  limite?: number;
  umbrales?: { warning?: number; danger?: number };
}

/**
 * Widget completo con datos y configuración
 */
export interface DashboardWidget {
  id: string;
  metrica: Metrica;
  titulo: string;
  tipo_widget: TipoWidget;
  configuracion?: WidgetConfig;
  orden: number;
  visible: boolean;
  datos?: MetricaValor;
}

/**
 * Datos para crear un nuevo widget
 */
export interface DashboardWidgetCreate {
  metrica_id: string;
  titulo_personalizado?: string;
  tipo_widget?: TipoWidget;
  configuracion?: WidgetConfig;
}

/**
 * Datos para actualizar un widget existente
 */
export interface DashboardWidgetUpdate {
  titulo_personalizado?: string;
  tipo_widget?: TipoWidget;
  configuracion?: WidgetConfig;
}

// ============================================================================
// INTERFACES PARA DASHBOARD
// ============================================================================

/**
 * Dashboard completo con todos sus widgets
 */
export interface Dashboard {
  id: string;
  nombre: string;
  descripcion?: string;
  es_default: boolean;
  es_compartido: boolean;
  periodo_default: PeriodoReporte;
  auto_refresh_segundos?: number;
  widgets: DashboardWidget[];
  updated_at: string;
}

/**
 * Datos para crear un nuevo dashboard
 */
export interface DashboardCreate {
  nombre: string;
  descripcion?: string;
  periodo_default?: PeriodoReporte;
  widgets?: DashboardWidgetCreate[];
  es_compartido?: boolean;
  auto_refresh_segundos?: number;
}

/**
 * Datos para actualizar un dashboard existente
 */
export interface DashboardUpdate {
  nombre?: string;
  descripcion?: string;
  periodo_default?: PeriodoReporte;
  layout?: Record<string, any>;
  es_compartido?: boolean;
  auto_refresh_segundos?: number;
}

/**
 * Elemento de lista de dashboards (resumen)
 */
export interface DashboardListItem {
  id: string;
  nombre: string;
  descripcion?: string;
  es_default: boolean;
  es_compartido: boolean;
  widget_count: number;
  updated_at: string;
}

// ============================================================================
// INTERFACES PARA REPORTES
// ============================================================================

/**
 * Filtros disponibles para generar reportes
 */
export interface FiltrosReporte {
  cliente_id?: string;
  usuario_id?: string;
  tipo_ticket?: string;
  estado_ticket?: string;
  prioridad?: string;
  modulo_erp?: string;
}

/**
 * Solicitud para generar un reporte
 */
export interface ReporteRequest {
  tipo: TipoReporte;
  periodo: PeriodoReporte;
  fecha_inicio?: string;
  fecha_fin?: string;
  filtros?: FiltrosReporte;
  metricas?: string[];
}

/**
 * Respuesta con los datos del reporte generado
 */
export interface ReporteResponse {
  id: string;
  nombre: string;
  tipo: TipoReporte;
  periodo: string;
  fecha_inicio: string;
  fecha_fin: string;
  filtros?: Record<string, any>;
  datos: Record<string, any>;
  generado_at: string;
}

/**
 * Reporte guardado en el sistema
 */
export interface ReporteGuardado {
  id: string;
  nombre: string;
  tipo: TipoReporte;
  periodo: PeriodoReporte;
  fecha_inicio: string;
  fecha_fin: string;
  formato_exportacion?: string;
  generado_at: string;
  expira_at?: string;
  created_at: string;
}

/**
 * Solicitud para guardar un reporte
 */
export interface GuardarReporteRequest {
  nombre: string;
  expira_dias?: number;
}

/**
 * Solicitud para exportar un reporte
 */
export interface ExportarReporteRequest {
  formato: 'pdf' | 'xlsx' | 'csv';
  incluir_graficos?: boolean;
}

// ============================================================================
// INTERFACES PARA DATOS DE REPORTES ESPECÍFICOS
// ============================================================================

/**
 * Resumen de métricas operativas
 */
export interface ResumenOperativo {
  periodo: { inicio: string; fin: string };
  tickets_creados: number;
  tickets_resueltos: number;
  horas_registradas: number;
  actividades_completadas: number;
}

/**
 * Reporte operativo completo con detalles de tickets, conversaciones y actividades
 */
export interface ReporteOperativo {
  resumen: ResumenOperativo;
  tickets: {
    creados: number;
    resueltos: number;
    por_estado: Record<string, number>;
    por_prioridad: Record<string, number>;
    tiempo_promedio_resolucion_min: number;
    sla_cumplido_pct: number;
    tendencia: Array<{ fecha: string; valor: number }>;
  };
  conversaciones: {
    activas: number;
    sin_respuesta: number;
    tiempo_promedio_respuesta_min: number;
  };
  tiempo: {
    horas_totales: number;
    porcentaje_facturable: number;
    tendencia: Array<{ fecha: string; valor: number }>;
  };
  actividades: {
    completadas: number;
    pendientes: number;
    vencidas: number;
  };
}

/**
 * Reporte gerencial con KPIs y métricas de equipo
 */
export interface ReporteGerencial {
  periodo: {
    actual: { inicio: string; fin: string };
    anterior: { inicio: string; fin: string };
  };
  kpis: {
    tickets_resueltos: { valor: number; anterior: number; variacion: number; direccion: string };
    sla_cumplido: { valor: number; anterior: number; variacion: number; direccion: string };
    tiempo_promedio_resolucion_min: number;
    horas_registradas: { valor: number; anterior: number; variacion: number; direccion: string };
  };
  equipo: {
    productividad: Array<{ usuario: string; valor: number }>;
    carga_trabajo: Array<{ usuario: string; tickets: number }>;
  };
  clientes: {
    horas_por_cliente: Array<{ cliente: string; horas: number }>;
  };
}

/**
 * Reporte específico de un cliente con sus métricas y tickets
 */
export interface ReporteCliente {
  cliente: { id: string; nombre: string };
  periodo: { inicio: string; fin: string };
  resumen: {
    tickets_abiertos: number;
    tickets_periodo: number;
    tickets_resueltos: number;
    conversaciones_activas: number;
    horas_dedicadas: number;
  };
  tickets: {
    por_estado: Record<string, number>;
    por_prioridad: Record<string, number>;
    tiempo_promedio_resolucion_min: number;
    sla_cumplido_pct: number;
    tendencia: Array<{ fecha: string; valor: number }>;
  };
  conversaciones: {
    activas: number;
    por_estado: Record<string, number>;
  };
  tiempo: {
    horas_totales: number;
  };
}
